import { IFunnelBuilder, IPopulateFunnelBuilder } from './types';
import { buildFunnelConfigForMode } from '../../../utils/builder';
import {
  populateReportData,
  removeSelectedPartitionAction,
  resetFunnelOrder,
  setBuilderFunnelModeAction,
  setBuilderOrderAction,
  setGroupbyAction,
  setSelectedPartitionAction,
  setSubjectAction,
} from './index';
import { batch } from 'react-redux';
import { AppThunk } from '../../store';
import { assertCondition } from '../../../utils/types';
import {
  FunnelModeKey,
  IPartitionValueFieldsFragment,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { isNil } from 'lodash';
import {
  isDayPartition,
  isMoreThanMonth,
  isMoreThanOneDay,
} from '../../../components/Builder/Partition/utils';
import { MaybeNil } from '../../../utils/interfaces';

/**
 * Bridges newBuilder and oldBuilder slices because setting funnel mode
 * potentially alters the default time unit (currently stored in newBuilder slice)
 *
 * Also ensures that dependencies between Subject / Partition and Order are met
 *
 * See https://kubitai.atlassian.net/wiki/spaces/ENG/pages/1867513857/Funnel+-+Mode+Dependencies
 *
 * @param payload same payload as setBuilderFunnelModeAction
 */

type ISetBuilderFunnelModeActionPayload = Parameters<
  typeof setBuilderFunnelModeAction
>['0'];

/**
 * Returns true if the partition value for conversions window is invalid according
 * to the business logic. This code kind of duplicates the code in
 * ConversionWindowInput component, but it is extremely hard to reuse the overall
 * validation
 */
const hasInvalidConversionWindowPartitionValue = (
  partitionValues: IPartitionValueFieldsFragment[],
  newMode: MaybeNil<FunnelModeKey>,
) => {
  const conversionWindow = partitionValues.find(
    (pv) => pv.partition.__typename === 'ConversionWindowPartition',
  );

  // no conversion window partition -> it is OK
  if (isNil(conversionWindow)) {
    return false;
  }

  // there is conversion window but the value is missing -> invalid
  if (isNil(conversionWindow.conversionWindow)) {
    return true;
  }

  const { timeUnit, value } = conversionWindow.conversionWindow;

  const isDayPartitionSelected = partitionValues.find((p) =>
    isDayPartition(p.partition),
  );

  return (
    (isDayPartitionSelected && isMoreThanOneDay(timeUnit, value)) ||
    (newMode === FunnelModeKey.TimeToConvert &&
      isMoreThanMonth(timeUnit, value))
  );
};

export const setBuilderFunnelModeThunk =
  (payload: ISetBuilderFunnelModeActionPayload): AppThunk =>
  (dispatch, getState) => {
    batch(() => {
      const { builder: oldBuilder } = getState();
      assertCondition(oldBuilder.queryType === QueryType.Funnel);
      const { funnelConfig, selectedOptions: currentSelectedOption } =
        oldBuilder as IFunnelBuilder;

      // select newConfig (new defaultOptions and new default selectedOption) based on the mode
      const newConfig = buildFunnelConfigForMode(funnelConfig, payload.newMode);
      const {
        defaultOptions: newDefaultOptions,
        selectedOptions: newDefaultSelectedOption,
      } = newConfig;

      // extract new default selection, we are going to use it later to set it as a default
      // if we detect that _current_ selectedOption is not among selectable _new_ defaultOptions
      const {
        funnelOrder: newDefaultFunnelOrder,
        groupby: newDefaultGroupBy,
        partitionValues: newDefaultPartitionValues,
        subject: newDefaultSubject,
        mode: newMode,
        ...restNewDefaultSelectedOption
      } = newDefaultSelectedOption;

      // Order
      // -----
      // Reset the selected order to the new default order if the current order
      // is no longer among the selectable options
      if (
        !newDefaultOptions.funnelOrders.some(
          (option) => option.key === currentSelectedOption.funnelOrder,
        )
      ) {
        dispatch(
          setBuilderOrderAction({
            newOrder: newDefaultFunnelOrder,
          }),
        );
      }

      // Group by
      // --------
      // Reset the selected groupby to the new default groupbys if the current groupby
      // is no longer among the selectable options
      if (
        !newDefaultOptions.groupbys.some(
          (option) => option.key === currentSelectedOption.groupby,
        ) &&
        !isNil(newDefaultGroupBy)
      ) {
        dispatch(
          setGroupbyAction({
            queryType: QueryType.Funnel,
            groupbyKey: newDefaultGroupBy,
          }),
        );
      }

      // Partitions
      // ----------
      // Partitions require quite an effort to be validated if they are
      // supported in the new mode. We validate 3 conditions:
      // 1. Is the partition value's kind is among the supported kinds
      //    (None, Field or Window)?
      // 2. If the partition value is a time window, does the value's unit is
      //    still selectable
      // 3. If the partition value is a time window, does it conform the limits

      // We require at least one selected partition, "no partition" is actually
      // a value with PartitionKind.None. If we remove the last partition,
      // the selection is replaced with nonePartitionValue but only if the mode
      // supports None
      const nonePartition = newDefaultOptions.partitions.find(
        (pv) => pv.__typename === 'NonePartition',
      );
      const nonePartitionValue: IPartitionValueFieldsFragment | null =
        nonePartition
          ? {
              __typename: 'PartitionValue',
              partition: nonePartition,
              conversionWindow: null,
            }
          : null;

      for (const currentPartitionValue of currentSelectedOption.partitionValues) {
        // [Condition 1] is there such partition in the new selectable partitions?
        const partitionInvalid = !newDefaultOptions.partitions.some(
          (option) => option.key === currentPartitionValue.partition.key,
        );

        // [Condition 2] is this a conversion window partition and is the time unit seletable?
        const conversionWindowTimeUnitInvalid =
          currentPartitionValue.partition.__typename ===
            'ConversionWindowPartition' &&
          !newDefaultOptions.partitionWindowTimeUnits.some(
            (timeUnit) =>
              timeUnit === currentPartitionValue.conversionWindow?.timeUnit,
          );

        // remove the partition if it is not valid
        if (partitionInvalid || conversionWindowTimeUnitInvalid) {
          dispatch(
            removeSelectedPartitionAction({
              value: currentPartitionValue,
              noneValue: nonePartitionValue,
            }),
          );
        }
      }

      const updatedPartitionValues = (getState().builder as IFunnelBuilder)
        .selectedOptions.partitionValues;

      // Not all modes allow None partition (e.g. Time To Convert)
      // If we are left with empty partitions and this mode does not support
      // Partition=None, then reset to the default partition
      // Also check for [Condition 3]
      if (
        (updatedPartitionValues.length === 0 && !nonePartition) ||
        hasInvalidConversionWindowPartitionValue(
          updatedPartitionValues,
          newMode,
        ) // [Condition 3]
      ) {
        for (const newDefaultPartitionValue of newDefaultPartitionValues) {
          dispatch(
            setSelectedPartitionAction({ value: newDefaultPartitionValue }),
          );
        }
      }

      // Subject
      // -------
      // Changing subject requires extra rest of the funnel orders because
      // Subject=Event only supports Order=Exact
      if (
        !newDefaultOptions.subjects.some(
          (s) => s.key === currentSelectedOption.subject,
        )
      ) {
        dispatch(
          setSubjectAction({
            queryType: QueryType.Funnel,
            subjectKey: newDefaultSubject || undefined,
          }),
        );
        dispatch(resetFunnelOrder());
      }

      // Remaining selectedOptions
      // -------------------------
      // The remaining newSelectedOptions (dateKind, funnelCustomOrder) don't
      // need special treatment and are set as is
      const bulkUpdateRemainingSelectedAndDefaultOptions: IPopulateFunnelBuilder =
        {
          queryType: QueryType.Funnel,
          defaultOptions: newDefaultOptions,
          selectedOptions: {
            ...restNewDefaultSelectedOption,
          },
        };
      dispatch(
        populateReportData(bulkUpdateRemainingSelectedAndDefaultOptions),
      );

      // Finally, change the mode property
      dispatch(setBuilderFunnelModeAction(payload));
    });
  };
